import React, { AnchorHTMLAttributes, ReactNode } from 'react';
import cntl from 'cntl';

// Define props to include navigation (href) and BaseButton styles (variant/size)
export interface LinkButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'md' | 'lg';
  className?: string;
  disabled?: boolean;
}

/**
 * Reuses the styling logic from BaseButton
 * to ensure the link visually inherits the button's design system.
 */
const getLinkButtonClasses = (
  variant: string,
  size: string,
  className: string,
) => cntl`
  inline-flex items-center justify-center
  rounded-md transition-all duration-200
  cursor-pointer no-underline
  focus:outline-none focus:ring-2 focus:ring-offset-2
  
  ${size === 'lg' ? 'px-4 py-3 text-sm font-semibold tracking-wider' : 'px-3 py-1.5 text-sm font-semibold'}
  
  ${variant === 'primary' ? 'bg-[#007ABC] text-white hover:bg-[#005f91] active:scale-95' : ''}
  ${variant === 'secondary' ? 'bg-[#B31942] text-white hover:bg-[#8e1435] active:scale-95' : ''}
  ${variant === 'ghost' ? 'bg-transparent hover:bg-black/5 active:bg-black/10' : ''}
  
  ${className}
`;

export const LinkButton = ({
  href,
  children,
  variant = 'primary',
  size = 'lg',
  className = '',
  disabled = false,
  ...props
}: LinkButtonProps) => {
  return (
    <a
      href={href}
      className={getLinkButtonClasses(variant, size, className)}
      aria-disabled={disabled}
      {...props}
    >
      {children}
    </a>
  );
};
